/**
 * Cómo se ENSEÑA el SLA de primera respuesta (paso 89). Solo presentación.
 *
 * Aquí no se cuenta ni una hora laborable: el reloj es UNO y vive en el
 * servidor (`app/sla/reloj.py`). Lo que llega por la API ya trae el
 * vencimiento (`firstResponseDueAt`), el momento del aviso del 25 %
 * (`firstResponseWarnAt`) y si se cumplió (`slaCumplido`, la regla de
 * `app/sla/estado.py`). Esto solo compara esas fechas con «ahora» y elige
 * texto y tono.
 *
 * D3 (Jose, 2026-10-06): si vence HOY, cuenta atrás («quedan 25 min»); si vence
 * otro día, el momento («vence lun 8:30»). Así nunca hay un número quieto todo
 * el fin de semana. Vencido, igual: «Vencido hace 20 min» hoy, «Vencido el
 * vie 17:00» si fue otro día.
 *
 * Las horas, en la zona del NAVEGADOR: es un momento, y quien mira lo lee en
 * su reloj. No se escribe Madrid a mano (K63).
 */
import type { Incidencia } from '../services/api';

/** ok = corre · warn = queda el 25 % · late = vencido o incumplido ·
 *  done = cumplido · none = no cuenta (cerrado a tiempo sin responder, o
 *  rechazado). Mismos cuatro estados que el diseño v7 (`slaColor`). */
export type SlaTono = 'ok' | 'warn' | 'late' | 'done' | 'none';

export interface SlaVista {
  texto: string;
  tono: SlaTono;
  /** Para el `title`: el vencimiento exacto y de dónde sale. */
  detalle: string;
}

const CERRADOS = new Set(['resuelta', 'rechazada']);
const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

function mismoDia(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function hora(d: Date): string {
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** «lun 8:30», «mañana 9:00» o «12/10 8:30» si cae a más de una semana
 *  (sin la hora con `conHoraLejana = false`: en la columna no cabe y, de
 *  algo vencido hace más de una semana, la hora ya no importa). */
function momento(d: Date, now: Date, conHoraLejana = true): string {
  const manana = new Date(now);
  manana.setDate(now.getDate() + 1);
  if (mismoDia(d, manana)) return `mañana ${hora(d)}`;
  const dias = Math.abs(d.getTime() - now.getTime()) / 86_400_000;
  if (dias < 6) return `${DIAS[d.getDay()]} ${hora(d)}`;
  const fecha = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  return conHoraLejana ? `${fecha} ${hora(d)}` : fecha;
}

/** Un plazo en palabras: «45 min», «1 h», «2 h 30 min». */
export function duracion(mins: number): string {
  const m = Math.max(0, Math.round(mins));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

/** «06/10 20:16», para una celda estrecha. */
export function fechaCorta(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${hora(d)}`;
}

function fechaLarga(d: Date): string {
  return d.toLocaleString('es-ES', {
    weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export function slaVista(item: Incidencia, nowMs: number): SlaVista | null {
  if (!item.firstResponseDueAt) return null;
  const now = new Date(nowMs);
  const vence = new Date(item.firstResponseDueAt);
  const origen = [
    item.slaPackNombre && `SLA ${item.slaPackNombre}`,
    item.slaPlazoMin && `${duracion(item.slaPlazoMin)} laborables`,
  ].filter(Boolean).join(', ');
  const detalle = `Primera respuesta: vence el ${fechaLarga(vence)}${origen ? ` (${origen})` : ''}`;

  if (item.firstResponseAt) {
    const resp = `${detalle}. Respondido el ${fechaLarga(new Date(item.firstResponseAt))}`;
    return item.slaCumplido
      ? { texto: 'Cumplido', tono: 'done', detalle: resp }
      : { texto: 'Fuera de plazo', tono: 'late', detalle: resp };
  }
  if (CERRADOS.has(item.status)) {
    // Cerrado sin respuesta (D4): si venció antes de cerrarse, incumplido; si
    // no, o si se rechazó, no cuenta.
    return item.slaCumplido === false
      ? { texto: 'Sin responder', tono: 'late', detalle: `${detalle}. Se cerró sin responder, ya vencido` }
      : { texto: '—', tono: 'none', detalle: `${detalle}. Se cerró sin responder: no cuenta` };
  }
  if (nowMs >= vence.getTime()) {
    // Vencido, los minutos dejan de importar a partir de la hora: «Vencido
    // hace 3 h». Y así el texto cabe en la columna (medido: 120 px a 12 px).
    const mins = (nowMs - vence.getTime()) / 60000;
    const texto = mismoDia(vence, now)
      ? `Vencido hace ${mins < 60 ? duracion(mins) : `${Math.floor(mins / 60)} h`}`
      : `Vencido el ${momento(vence, now, false)}`;
    return { texto, tono: 'late', detalle };
  }
  const aviso = item.firstResponseWarnAt ? new Date(item.firstResponseWarnAt).getTime() : Infinity;
  const tono: SlaTono = nowMs >= aviso ? 'warn' : 'ok';
  const texto = mismoDia(vence, now)
    ? `quedan ${duracion((vence.getTime() - nowMs) / 60000)}`
    : `vence ${momento(vence, now)}`;
  return { texto, tono, detalle };
}
